import { useQuery } from '@apollo/client'
import { Link, Navigate } from 'react-router-dom'
import { Loader } from '../../components'
import { Avatar } from '../../elements'
import { GET_CREATOR_BY_UUID, GetCreatorByUuidResponse } from '../../gql'
import { Header } from './components/Header'
import { CreatorReviewsPublic } from './CreatorReviewsPublic'
import { QueryGetCreatorByUuidArgs } from '../../gql/types'
import { ProfileCard } from './components/ProfileCard'
import { PortfolioGridNew } from '../../components/PortfolioGridNew'

export const CreatorPublic = ({
  view,
  uuid,
}: {
  view: string
  uuid: string
}) => {
  // Basic creator info query to show header
  const { loading, error, data } = useQuery<
    GetCreatorByUuidResponse,
    QueryGetCreatorByUuidArgs
  >(GET_CREATOR_BY_UUID, {
    variables: {
      uuid,
    },
  })

  if (error) {
    console.log(error)
    return <p>There has been an error. Try refreshing the page</p>
  }

  if (loading) {
    return <Loader />
  }

  if (!loading && !data?.getCreatorByUuid) {
    return <p>Creator does not exist.</p>
  }

  const creator = data?.getCreatorByUuid

  // Validate view parameter
  if (view !== 'profile' && view !== 'reviews' && view !== 'portfolio') {
    // Redirect to profile view by default
    return <Navigate to={`/creators/${uuid}/profile`} replace />
  }

  return (
    <div className='min-h-screen bg-gray-50 pb-10'>
      {/* Header/Navbar */}
      <Header />

      {/* Main content */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6'>
        <div className='bg-white rounded-lg shadow overflow-hidden'>
          {/* Creator info header (shared between both views) */}
          <div className='flex flex-col px-6 border-b border-gray-200'>
            <div className='grid grid-cols-1 lg:flex lg:flex-row lg:justify-between py-2'>
              <div className='col-span-1'>
                <div className='flex items-center'>
                  {creator?.avatar?.url && (
                    <div className='mr-3'>
                      <Avatar
                        type='user'
                        src={creator.avatar?.url}
                        size='md'
                        alt={creator.firstName}
                      />
                    </div>
                  )}
                  <div>
                    <div className='font-bold text-3xl font-outfit'>
                      {creator?.firstName} {creator?.lastName}
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation tabs */}
              <div className='flex items-center mt-4 lg:mt-0 space-x-4'>
                <Link
                  to={`/creators/${uuid}/profile`}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    view === 'profile'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Profile
                </Link>
                <Link
                  to={`/creators/${uuid}/portfolio`}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    view === 'portfolio'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Portfolio
                </Link>
                <Link
                  to={`/creators/${uuid}/reviews`}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    view === 'reviews'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Reviews
                </Link>
              </div>
            </div>
          </div>

          {/* Render appropriate view based on the URL parameter */}
          {view === 'profile' && <ProfileCard creator={creator} />}
          {view === 'reviews' && (
            <CreatorReviewsPublic creatorUuid={uuid} creator={creator} />
          )}
          {view === 'portfolio' && (
            <PortfolioGridNew creator={creator} limitAndUseSlider={false} />
          )}
        </div>
      </div>
    </div>
  )
}
